"use strict";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
Object.defineProperty(exports, "__esModule", { value: true });
const Types_1 = require("../utils/Types");
class SignalingAndMetricsConnectionMonitor {
    constructor(audioVideoController, realtimeController, connectionHealthData, pingPong, statsCollector) {
        this.audioVideoController = audioVideoController;
        this.realtimeController = realtimeController;
        this.connectionHealthData = connectionHealthData;
        this.pingPong = pingPong;
        this.statsCollector = statsCollector;
        this.isActive = false;
        this.hasSeenValidPacketMetricsBefore = false;
        this.lastTotalPacketsReceived = 0;
        this.realtimeController.realtimeSubscribeToLocalSignalStrengthChange((signalStrength) => {
            if (this.isActive) {
                this.receiveSignalStrengthChange(signalStrength);
            }
        });
    }
    start() {
        this.isActive = true;
        this.pingPong.addObserver(this);
        this.pingPong.start();
        this.audioVideoController.addObserver(this);
    }
    stop() {
        this.isActive = false;
        this.pingPong.removeObserver(this);
        this.pingPong.stop();
        this.audioVideoController.removeObserver(this);
    }
    receiveSignalStrengthChange(signalStrength) {
        if (signalStrength === 0) {
            this.connectionHealthData.setLastNoSignalTimestampMs(Date.now());
        }
        else if (signalStrength <= 0.5) {
            this.connectionHealthData.setLastWeakSignalTimestampMs(Date.now());
        }
        else {
            this.connectionHealthData.setLastGoodSignalTimestampMs(Date.now());
        }
        this.updateConnectionHealth();
    }
    didReceivePong(_id, latencyMs, clockSkewMs) {
        this.connectionHealthData.setConsecutiveMissedPongs(0);
        this.statsCollector.logLatency('ping_pong', latencyMs);
        this.statsCollector.logLatency('ping_pong_clock_skew', clockSkewMs);
        this.updateConnectionHealth();
    }
    didMissPongs() {
        this.connectionHealthData.setConsecutiveMissedPongs(this.connectionHealthData.consecutiveMissedPongs + 1);
        this.updateConnectionHealth();
    }
    metricsDidReceive(clientMetricReport) {
        let audioPacketsReceived = 0;
        let audiofractionPacketsLostInbound = 0;
        const metricReport = clientMetricReport.getObservableMetrics();
        const potentialAudioPacketsReceived = metricReport.audioPacketsReceived;
        const potentialAudioFractionPacketsLostInbound = metricReport.audioPacketsReceivedFractionLoss;
        const audioSpeakerDelayMs = metricReport.audioSpeakerDelayMs;
        // Firefox does not presently have aggregated bandwidth estimation
        if (typeof audioSpeakerDelayMs === 'number' && !isNaN(audioSpeakerDelayMs)) {
            this.connectionHealthData.setAudioSpeakerDelayMs(audioSpeakerDelayMs);
        }
        // To get the total packets received, including RTCP, we need to sum up
        // all candidate pair metrics (in case the candidate pair changes).
        //
        // The stats collector currently doesn't account for candidate pair stats
        // so we just use the raw report for now.
        const webrtcReport = clientMetricReport.getRTCStatsReport();
        let totalPacketsReceived = 0;
        webrtcReport.forEach(stat => {
            if (stat.type === 'candidate-pair' && stat.packetsReceived) {
                totalPacketsReceived += stat.packetsReceived;
            }
        });
        const packetsReceived = totalPacketsReceived - this.lastTotalPacketsReceived;
        this.lastTotalPacketsReceived = totalPacketsReceived;
        if (typeof potentialAudioPacketsReceived === 'number' &&
            typeof potentialAudioFractionPacketsLostInbound === 'number') {
            audioPacketsReceived = potentialAudioPacketsReceived;
            audiofractionPacketsLostInbound = potentialAudioFractionPacketsLostInbound;
            if (audioPacketsReceived < 0 || audiofractionPacketsLostInbound < 0 || packetsReceived < 0) {
                // The stats collector or logic above may emit negative numbers on this metric after reconnect
                // which we should not use.
                return;
            }
        }
        else {
            return;
        }
        this.addToMinuteWindow(this.connectionHealthData.packetsReceivedInLastMinute, audioPacketsReceived);
        this.addToMinuteWindow(this.connectionHealthData.fractionPacketsLostInboundInLastMinute, audiofractionPacketsLostInbound);
        if (packetsReceived > 0) {
            this.hasSeenValidPacketMetricsBefore = true;
            this.connectionHealthData.setConsecutiveStatsWithNoPackets(0);
        }
        else if (this.hasSeenValidPacketMetricsBefore) {
            this.connectionHealthData.setConsecutiveStatsWithNoPackets(this.connectionHealthData.consecutiveStatsWithNoPackets + 1);
        }
        if (audioPacketsReceived === 0 || audiofractionPacketsLostInbound > 0) {
            this.connectionHealthData.setLastPacketLossInboundTimestampMs(Date.now());
        }
        if (typeof metricReport.audioPacketsSent === 'number') {
            this.updateAudioPacketsSentInConnectionHealth(metricReport.audioPacketsSent);
        }
        this.updateConnectionHealth();
    }
    updateAudioPacketsSentInConnectionHealth(audioPacketsSent) {
        if (audioPacketsSent > 0) {
            this.connectionHealthData.setConsecutiveStatsWithNoAudioPacketsSent(0);
        }
        else {
            this.connectionHealthData.setConsecutiveStatsWithNoAudioPacketsSent(this.connectionHealthData.consecutiveStatsWithNoAudioPacketsSent + 1);
        }
    }
    addToMinuteWindow(array, value) {
        array.unshift(value);
        if (array.length > 60) {
            array.pop();
        }
    }
    updateConnectionHealth() {
        this.audioVideoController.forEachObserver((observer) => {
            Types_1.Maybe.of(observer.connectionHealthDidChange).map(f => f.bind(observer)(this.connectionHealthData.clone()));
        });
    }
}
exports.default = SignalingAndMetricsConnectionMonitor;
